/*
 * Copyright (c) 2022. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* windowmanager.ts
* 6-12-2019 - Jelmer Jellema - Spin in het Web B.V.
*
* windowmanager: een container voor windows, met api
* richting de controlers (maken / verwijderen windows etc) en
* richting de windows zelf
* */

import {Component, ElementRef, HostListener, Type, ViewChild} from '@angular/core';
import {Sihwlog, hash} from 'sihw-ngx';
import {WindowComponent} from '../window/window.component';
import {WindowedComponent} from '../windowedcomponent.class';
import {isObject} from 'lodash-es';

interface WindowDef {
  handle: string,
  componentType: Type<WindowedComponent>,
  inputs: any,
  window: WindowComponent,
  depend?: WindowComponent, //window waarvan dit window afhankelijk is qua z-order,
  closeDepend?: boolean, //als true dan sluit dit window als het window sluit waarvan het afhankelijk is
  closeResolver: (result: any) => {} //resolve bij sluit met een eventueel resultaat
}

/**
 * Opties bij create
 */
export interface WindowCreateOptions {
  depend?: WindowComponent, //scherm is dependant van dit window
  closeDepend?: boolean //als het hoofdwindow (depend) sluit, sluit dit window ook
}

@Component({
  selector: 'windowmanager',
  templateUrl: './windowmanager.html',
  styleUrls: ['./windowmanager.scss']
})
export class SihwWindowmanager {
  @ViewChild('windowboundcontainer', {static: true}) public container: ElementRef; //ook opgevraagd door windows die hun plek moeten weten

  ///de window-boekhouding
  //we werken keurig met een array, super simpel. Dat betekent dat
  //verwijderen wat lastig gaat, maar de sorteervolgorde gewoon de z-index oplevert
  //dus geen map
  public _windows: WindowDef[] = []; //De windowdefinities
  private _windowsChrono: WindowComponent[] = []; //Voor naar buiten toe de lijst met windows
  private nextHandle = 1;


  //log
  private log: Sihwlog;

  //timers etc
  private timers: any = {}; //1 var voor lokale timers

  constructor() {
    this.log = new Sihwlog('debug', 'Windowmanager');
  }

  //helper voor doorgeven van de manager (wij dus) aan een window via inputs
  get manager(): SihwWindowmanager {
    return this;
  }


  ////// Public service ////////////
  /**
   * Maak een nieuw window
   * @param component
   * @param [data] hash met inputvariabelen, elk van die variabelen wordt gezet
   * @param [options] speciale opties, zie WindowCreateOptions
   * @returns Promise<any> promise van sluiten, eventueel met resultaat
   */
  create(component: Type<WindowedComponent> | string, data: hash<any> = {}, options: WindowCreateOptions = {}): Promise<any> {
    let id = `win_${this.nextHandle++}`;
    let resolver =null;
    let resPromise = new Promise((resolve, _reject) => {
      resolver = resolve;
    });

    //is het component een string of al een component?
    if (!isObject(component)) {
      let compObj = SihwWindowmanager.components[<string> component];  //van naam naar class
      if (!compObj) {
        this.log.debug(SihwWindowmanager.components);
        throw Error(`Onbekend windowtype ${component}`);
      }
      component = compObj;
    }

    let def: WindowDef = {
      handle: id,
      componentType: <Type<WindowedComponent>> component, //want al omgezet
      inputs: data,
      window: <WindowComponent> null, //wordt via callback gezet, zie setwindow
      depend: options.depend,
      closeDepend: !!options.closeDepend,
      closeResolver: resolver
    };

    //eventueel actief window verliest zijn focus
    let actief = this.actiefWindow;
    if (actief)
    {
      actief.losingFocus();
    }
    this._windows.push(def); //rebuildchrono pas als hij er is (zie setwindow)

    resPromise.then(res => {
      this.log.debug(`Window close`, res);
    });

    return resPromise; //geef de promise terug
  }

  /**
   * Window meldt zich.
   * @param handle
   * @param w
   */
  public setWindow(handle: string, w: WindowComponent) {
    let def = this._windows.find(w => w.handle === handle);
    if (!def) {
      this.log.warn(`Setwindow: windowdef niet gevonden`, handle, this._windows);
      return;
    }
    def.window = w; //gezet

    //nu het window verder initialiseren

    //hoe groot zijn we?
    let rect = this.container.nativeElement.getBoundingClientRect();
    if (rect.width < 1000) {
      //full screen
      void w.maximize();
    }

    //even de tijd voor het rebuilden, anders is het een ExpressionChangedAfterItHasBeenCheckedError


    setTimeout(() => {
      this.rebuildChrono(); //toevoegen aan de windows() getter
      this.actiefWindow.gettingFocus(); //nu wel
    });
  }

  /**
   * Geeft een lijst met windowProxys terug voor de windows, op chronologische volgorde
   */
  public get windows(): WindowComponent[] {
    return this._windowsChrono;
  }


  ////////////////// public service //////////////////

  /**
   * Check of het gegeven window actief is
   * @param window
   */
  public actief(window: WindowComponent): boolean {
    //is dit het actieve window?
    return window === this.actiefWindow;
  }

  /**
   * Geef de actieve windowdef terug, of null
   */
  public get actiefWindowDef(): WindowDef {
    return this._windows.length ? this._windows[this._windows.length - 1] : null;
  }

  /**
   * Geef het actieve window terug, of null
   */
  public get actiefWindow(): WindowComponent {
    let def = this.actiefWindowDef;
    return def ? def.window : null;
  }

  public get numWindows(): number {
    return this._windows.length;
  }

  /**
   * Sluit een window als dat er is.
   */
  close(window: WindowComponent, result: any = null): void {
    let i = this.windowIndex(window);
    if (i >= 0) {
      let def = this.windowDef(window);
      //meldt het het window
      window.closing();
      //en de closepromise resolven
      try {
        def.closeResolver(result);
      } catch (e) {
        this.log.error(e);
      }

      //gewoon weg
      this._windows.splice(i, 1);

    }
    for (let ander of this._windows.filter(w => w.depend === window)) {
      //ook weg?
      if (ander.closeDepend) {
        this.close(ander.window);
      } else {
        ander.depend = null;
      }
    }
    this.rebuildChrono();
    let actief = this.actiefWindow;
    if (actief) {
      actief.gettingFocus(); //dat moet deze even weten
    }
  }

  closeAll(): void {
    //keurig allemaal sluiten, zodat promisses resolven
    for (let w of this._windows) {
      this.close(w.window);
    }
    this._windows = [];
    this._windowsChrono = [];
  }


  ///////////////// Private ////////////////
  //geeft de index in de windows-lijst gegeven een window
  //-1 bij niet gevonden
  private windowIndex(window: WindowComponent): number {
    let wnd = this.windowDef(window);
    return wnd ? this._windows.indexOf(wnd) : -1;
  }

  /**
   * Geeft het definitie-object uit onze _windows
   * @param window
   */
  private windowDef(window: WindowComponent): WindowDef {
    return this._windows.find(w => w.window === window);
  }

  /**
   * Herbouw de strikt chronologische lijst, van windows. Voor de buitenwereld. Dat gebeurt op handle
   */
  private rebuildChrono() {
    let defkopie = [...this._windows];
    defkopie.sort((d1, d2) => (d1.handle < d2.handle ? -1 : 1));
    this._windowsChrono = defkopie.map(w => w.window); //kopieer de windows
  }


  ////////////////// INTERNE EVENTS ///////////////////
  @HostListener('window:resize', ['$event'])
  onResize(_event) {
    if (this.timers.resizeTimer) {
      clearTimeout(this.timers.resizeTimer);
    }
    this.timers.resizeTimer = setTimeout(() => {
      for (let d of this._windows) {
        if (d.window) {
          d.window.fixPosition();
        }
      }
      this.timers.resizeTimer = null;
    }, 200); //pas aan het eind van de resize
  }

  /**
   *
   * mousedown / touchstart event - haal een window naar voren door hem achterin de z-order te zetten
   */
  naarVoren(window: WindowComponent): void {
    if (this.actief(window)) {
      return;
    }

    this.actiefWindow.losingFocus(); //is er gegarandeerd
    //letterlijk schuiven in _ windows
    let i = this.windowIndex(window);
    if (i >= 0) {
      let wnd = this._windows[i];
      this._windows.splice(i, 1);
      this._windows.push(wnd);
      this.updateDependents();
    }
    //pas na het bijwerken van de dependents weten we wie actief is. Die heeft dus focus. Dat kan dezelfde zijn als die het was. Dan moet het toch opnieuw
    //omdat de browser wel degelijk focus heeft weggenomen van controls
    //hooks
    //dus niet het window dat werd aangeklikt, maar het window dat nu actief is
    this.actiefWindow.gettingFocus();
  }

  /**
   * Verzoek van window of onszelf om de dependents weer keurig voor hun depend te zetten (als die er nog is)
   */
  updateDependents() {
    //loop ze af
    let newwindows = [];
    let todo = [...this._windows]; //kopie
    while (todo.length) {
      let volgendeRonde = [];
      for (let w of todo) {
        if (!w.depend) {
          newwindows.push(w); //deze kan er gewoon op
          continue;
        }
        //anders: is onze depend er al? Dan kunnen wij ook
        let i = this.windowIndex(w.depend);
        if (i >= 0 && newwindows.includes(this._windows[i])) {
          //okee, onze dependent is er al
          newwindows.push(w);
          continue;
        }
        //ook niet. Deze kan nog niet
        volgendeRonde.push(w);
      }
      //einde for loop
      todo = volgendeRonde;
    }
    this._windows = newwindows;
  }


  //////////////////////////////////// STATIC /////////////////////////////


  private static components: hash<Type<WindowedComponent>> = {}; //map string op classes, handmatig in de schermclasses te doen

  /**
   * simpele call in window-classes: SihwWindowmanager.register('mijnscherm',MijnSchermClass)
   * @param name
   * @param Component
   */
  public static register(name: string, Component: Type<WindowedComponent>) {
    SihwWindowmanager.components[name] = Component;
  }
}
